import { Button, Divider } from "@mui/material"
import { Dayjs } from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"
import InputDateField from "src/components/atom/InputDateField"
import InputNameField from "src/components/atom/InputNameField"
import InputTextField from "src/components/atom/InputTextField"
import SelectField from "src/components/atom/SelectField"
import { useLoginContext } from "src/hooks/useLoginContext"
import { GENDER, PROVINCES } from "src/modules/const"
import { getCountriesForSelect } from "src/modules/helper"
import { Box } from "src/UILibrary"

type Props = {
  firstName: string
  lastName: string
  firstNameJP: string
  lastNameJP: string
  handleFirstName: any
  handleLastName: any
  handleFirstNameJP: any
  handleLastNameJP: any
  birthday: Dayjs | null
  handleBirthday: any
  gender: string
  handleGener: any
  nation: string
  handleNation: any
  areaId: number
  handleAreaId: any
  phoneNumber: string
  handlePhoneNumber: any
  postCode: string
  handlePostCode: any
  province: string
  handleProvince: any
  city: string
  handleCity: any
  street: string
  handleStreet: any
  building: string
  handleBuilding: any
  nameValidationMessage: string
  birthdayValidationMessage: string
  phoneNumberValidationMessage: string
  postCodeValidationMessage: string
  cityValidationMessage: string
  handlePostCodeInput: any
  handleNext: () => void
}

export const UserGeneralInformation = ({
  firstName,
  lastName,
  firstNameJP,
  lastNameJP,
  handleFirstName,
  handleLastName,
  handleFirstNameJP,
  handleLastNameJP,
  birthday,
  handleBirthday,
  gender,
  handleGener,
  nation,
  handleNation,
  areaId,
  handleAreaId,
  phoneNumber,
  handlePhoneNumber,
  postCode,
  handlePostCode,
  province,
  handleProvince,
  city,
  handleCity,
  street,
  handleStreet,
  building,
  handleBuilding,
  nameValidationMessage,
  birthdayValidationMessage,
  phoneNumberValidationMessage,
  postCodeValidationMessage,
  cityValidationMessage,
  handlePostCodeInput,
  handleNext,
}: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.user" })

  const { areas } = useLoginContext()

  return (
    <Box maxWidth="md">
      <InputNameField
        name={t("name")}
        required
        firstName={firstName}
        lastName={lastName}
        firstNameJP={firstNameJP}
        lastNameJP={lastNameJP}
        handleFirstName={handleFirstName}
        handleLastName={handleLastName}
        handleFirstNameJP={handleFirstNameJP}
        handleLastNameJP={handleLastNameJP}
        validationMessage={nameValidationMessage}
      ></InputNameField>
      <InputDateField
        name={t("birthday")}
        required
        sx={{ mt: "40px" }}
        date={birthday}
        handleDate={handleBirthday}
        validationMessage={birthdayValidationMessage}
      ></InputDateField>
      <SelectField
        name={t("sex")}
        required
        sx={{ mt: "40px", width: "200px" }}
        selected={gender}
        handleSelected={handleGener}
        datas={[
          { label: t("male"), value: GENDER.MALE },
          { label: t("female"), value: GENDER.FEMALE },
        ]}
      ></SelectField>
      <SelectField
        name={t("nation")}
        required
        sx={{ mt: "40px", width: "355px" }}
        selected={nation}
        handleSelected={handleNation}
        datas={getCountriesForSelect()}
      ></SelectField>
      <SelectField
        name={t("address")}
        required
        sx={{ mt: "40px", width: "200px" }}
        selected={areaId}
        handleSelected={handleAreaId}
        datas={areas}
      ></SelectField>
      <InputTextField
        name={t("phone_number")}
        required
        sx={{ mt: "40px" }}
        placeholder={t("input_normal_string")}
        text={phoneNumber}
        handleText={handlePhoneNumber}
        validationMessage={phoneNumberValidationMessage}
        maxLength={15}
      ></InputTextField>
      <Divider sx={{ mt: "80px" }}></Divider>
      <InputTextField
        name={t("post_code")}
        required
        sx={{ mt: "80px" }}
        placeholder={t("input_normal_string")}
        text={postCode}
        handleText={handlePostCode}
        validationMessage={postCodeValidationMessage}
        handleFocusOut={handlePostCodeInput}
        maxLength={10}
      ></InputTextField>
      <SelectField
        name={t("province")}
        required
        sx={{ mt: "40px", width: "200px" }}
        selected={province}
        handleSelected={handleProvince}
        datas={PROVINCES}
      ></SelectField>
      <InputTextField
        name={t("city")}
        required
        sx={{ mt: "40px" }}
        text={city}
        handleText={handleCity}
        validationMessage={cityValidationMessage}
        maxLength={50}
      ></InputTextField>
      <InputTextField
        name={t("street")}
        sx={{ mt: "40px" }}
        inputStyle={{ width: "100%" }}
        text={street}
        handleText={handleStreet}
        maxLength={100}
      ></InputTextField>
      <InputTextField
        name={t("building")}
        sx={{ mt: "40px" }}
        inputStyle={{ width: "100%" }}
        text={building}
        handleText={handleBuilding}
        maxLength={100}
      ></InputTextField>
      <Button
        variant="contained"
        sx={{
          minWidth: "240px",
          py: "16px",
          px: "88px",
          fontSize: "1rem",
          fontFamily: "Noto Sans JP !important",
          lineHeight: "1rem",
          fontWeight: 700,
          my: "80px",
          mx: "auto",
          display: "flex",
          justifyContent: "center",
          borderRadius: 0,
          ":hover": {
            bgcolor: "#2DA389",
          },
        }}
        onClick={() => handleNext()}
      >
        {t("next")}
      </Button>
    </Box>
  )
}
